import { useParams, Link } from 'react-router-dom';

interface Invoice {
  id: string;
  number: string;
  amount: string;
  status: 'paid' | 'pending' | 'overdue';
  date: string;
}

interface Bid {
  id: string;
  title: string;
  amount: string;
  status: 'submitted' | 'accepted' | 'declined';
  date: string;
}

const MOCK_INVOICES: Invoice[] = [
  { id: '1', number: 'INV-1042', amount: '$3,200', status: 'paid', date: '2026-02-15' },
  { id: '2', number: 'INV-1038', amount: '$2,800', status: 'pending', date: '2026-02-01' },
  { id: '3', number: 'INV-1029', amount: '$4,100', status: 'paid', date: '2026-01-10' },
];

const MOCK_BIDS: Bid[] = [
  { id: '1', title: 'Corporate Brand Video', amount: '$5,500', status: 'accepted', date: '2026-01-20' },
  { id: '2', title: 'Product Launch Promo', amount: '$3,800', status: 'submitted', date: '2026-02-22' },
];

function statusBadge(status: string) {
  const colors: Record<string, { bg: string; fg: string }> = {
    paid: { bg: 'rgba(34,197,94,0.15)', fg: 'var(--color-success)' },
    pending: { bg: 'rgba(245,158,11,0.15)', fg: 'var(--color-warning)' },
    overdue: { bg: 'rgba(239,68,68,0.15)', fg: 'var(--color-danger)' },
    submitted: { bg: 'rgba(59,130,246,0.15)', fg: 'var(--color-info)' },
    accepted: { bg: 'rgba(34,197,94,0.15)', fg: 'var(--color-success)' },
    declined: { bg: 'rgba(239,68,68,0.15)', fg: 'var(--color-danger)' },
  };
  const c = colors[status] ?? { bg: 'rgba(139,143,163,0.15)', fg: 'var(--color-text-muted)' };

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '999px',
        fontSize: '0.75rem',
        fontWeight: 500,
        background: c.bg,
        color: c.fg,
        textTransform: 'capitalize',
      }}
    >
      {status}
    </span>
  );
}

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ marginBottom: '24px', fontSize: '0.875rem' }}>
        <Link to="/clients" style={{ color: 'var(--color-text-muted)' }}>
          Clients
        </Link>
        <span style={{ margin: '0 8px', color: 'var(--color-text-muted)' }}>/</span>
        <span>Client Details</span>
      </div>

      {/* Client info card */}
      <div
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '4px' }}>
              Client #{id}
            </h1>
            <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', marginBottom: '16px' }}>
              Member since January 2025
            </p>
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '2px' }}>Email</div>
                <div style={{ fontSize: '0.875rem' }}>client@example.com</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '2px' }}>Phone</div>
                <div style={{ fontSize: '0.875rem' }}>(555) 123-4567</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '2px' }}>Company</div>
                <div style={{ fontSize: '0.875rem' }}>Horizon Films</div>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              style={{
                padding: '8px 16px',
                background: 'transparent',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-text)',
                fontSize: '0.875rem',
              }}
            >
              Edit
            </button>
            <Link
              to="/invoices/new"
              style={{
                padding: '8px 16px',
                background: 'var(--color-primary)',
                borderRadius: 'var(--radius-md)',
                color: '#fff',
                fontSize: '0.875rem',
                fontWeight: 500,
                textDecoration: 'none',
              }}
            >
              New Invoice
            </Link>
          </div>
        </div>
      </div>

      {/* Two-column layout for invoices and bids */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
        {/* Linked Invoices */}
        <div
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: '24px',
          }}
        >
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '16px' }}>
            Invoices
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {MOCK_INVOICES.map((inv) => (
              <div
                key={inv.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  background: 'var(--color-bg)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{inv.number}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{inv.date}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{inv.amount}</span>
                  {statusBadge(inv.status)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Linked Bids */}
        <div
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: '24px',
          }}
        >
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '16px' }}>
            Bids
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {MOCK_BIDS.map((bid) => (
              <div
                key={bid.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  background: 'var(--color-bg)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{bid.title}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{bid.date}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{bid.amount}</span>
                  {statusBadge(bid.status)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
